import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { calculBySections, sectionsBatiKal, type Sections } from '@batical/calculPage/src/SectionCalculList'
import { images } from '@batical/common'
import { sectionColors } from '@batical/design'
import { setCalcul, setSection } from '@batical/reducer/src'
import { CalculCodeStateKeys } from '@batical/types/src'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import SearchIcon from '@mui/icons-material/Search'
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha'
import {
  Box,
  Container,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { FaChevronRight } from 'react-icons/fa'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Page from 'src/components/Page'

export type CalculCatalogEntry = {
  sectionCode: Sections
  calculCode: CalculCodeStateKeys
}

type GroupMode = 'section' | 'alpha'

const PAGE_SIZE = 12

const ProductImgStyle = styled('img')({
  width: 44,
  height: 44,
  borderRadius: 10,
  objectFit: 'cover' as const,
  flexShrink: 0
})

const CalculRowCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
  overflow: 'hidden',
  outline: 'none',
  '&:hover': {
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
    borderColor: alpha(theme.palette.primary.main, 0.35),
    transform: 'translateY(-1px)'
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.35)}`
  }
}))

const SECTION_ICON_ALPHA = 0.5

function normalizeSearch(s: string) {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
}

function buildCatalog(): CalculCatalogEntry[] {
  const out: CalculCatalogEntry[] = []
  for (const sec of sectionsBatiKal) {
    for (const c of calculBySections[sec]?.calculs ?? []) {
      out.push({ sectionCode: sec, calculCode: c })
    }
  }
  return out
}

const CATALOG = buildCatalog()

function groupPageBySection(pageItems: CalculCatalogEntry[]) {
  const g: Record<string, CalculCatalogEntry[]> = {}
  const keys: string[] = []
  for (const item of pageItems) {
    const key = item.sectionCode
    if (!g[key]) {
      g[key] = []
      keys.push(key)
    }
    g[key]!.push(item)
  }
  return { keys, g }
}

const AllCalculsPage = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [filter, setFilter] = useState('')
  const [groupMode, setGroupMode] = useState<GroupMode>('section')
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [filter, groupMode])

  const filtered = useMemo(() => {
    const needle = normalizeSearch(filter.trim())
    if (!needle) return CATALOG
    return CATALOG.filter(({ sectionCode, calculCode }) => {
      const haystack = [t(calculCode), t(sectionCode)].join(' ').toLowerCase()
      return normalizeSearch(haystack).includes(needle)
    })
  }, [filter, t])

  const orderedEntries = useMemo(() => {
    const byCalculName = (a: CalculCatalogEntry, b: CalculCatalogEntry) =>
      t(a.calculCode).localeCompare(t(b.calculCode), 'fr', { sensitivity: 'base' })

    if (groupMode === 'alpha') {
      return [...filtered].sort(byCalculName)
    }
    const out: CalculCatalogEntry[] = []
    for (const sec of sectionsBatiKal) {
      const block = filtered.filter(e => e.sectionCode === sec).sort(byCalculName)
      out.push(...block)
    }
    return out
  }, [filtered, groupMode, t])

  const totalCount = orderedEntries.length
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const pageItems = useMemo(
    () => orderedEntries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [orderedEntries, page]
  )

  const pageSectionSlice = useMemo(
    () => (groupMode === 'section' ? groupPageBySection(pageItems) : null),
    [groupMode, pageItems]
  )

  const rangeLabel = useMemo(() => {
    if (totalCount === 0) return null
    const from = (page - 1) * PAGE_SIZE + 1
    const to = Math.min(page * PAGE_SIZE, totalCount)
    return { from, to }
  }, [page, totalCount])

  const openCalcul = useCallback(
    (entry: CalculCatalogEntry) => {
      dispatch(setSection(entry.sectionCode))
      dispatch(setCalcul(entry.calculCode))
      navigate(`/dashboard/${entry.calculCode}`)
    },
    [dispatch, navigate]
  )

  const onRowKeyDown = useCallback((event: React.KeyboardEvent, entry: CalculCatalogEntry) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openCalcul(entry)
    }
  }, [openCalcul])

  const renderRow = (entry: CalculCatalogEntry) => {
    const { sectionCode, calculCode } = entry
    const imgUrl = images[sectionCode]
    const tint = sectionColors[sectionCode] ?? '#4277b9'
    const iconBg = alpha(tint, SECTION_ICON_ALPHA)

    return (
      <CalculRowCard
        key={`${sectionCode}-${calculCode}`}
        elevation={0}
        role="button"
        tabIndex={0}
        aria-label={`Ouvrir le calcul ${t(calculCode)}`}
        onClick={() => openCalcul(entry)}
        onKeyDown={e => onRowKeyDown(e, entry)}
        sx={{ cursor: 'pointer' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            p: 2,
            minHeight: 64
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
            {imgUrl ? (
              <ProductImgStyle src={imgUrl} alt="" style={{ backgroundColor: iconBg }} />
            ) : (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  flexShrink: 0,
                  backgroundColor: iconBg
                }}
              />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.35 }}>
                {t(calculCode)}
              </Typography>
            </Box>
          </Box>
          <FaChevronRight
            aria-hidden
            color={tint}
            size={20}
            style={{ flexShrink: 0, opacity: 0.85 }}
          />
        </Box>
      </CalculRowCard>
    )
  }

  return (
    <Page title="BatiKal | Calculs">
      <Box sx={{ bgcolor: '#ffffff', minHeight: '100%', pb: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 2.5 }, pb: 0 }}>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              mb: { xs: 2.5, md: 3 },
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              background: `linear-gradient(100deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.075)} 34%, ${alpha(theme.palette.primary.main, 0.03)} 55%, ${theme.palette.background.paper} 85%)`,
              overflow: 'hidden'
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { md: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 1.25, md: 2 },
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 1.35, md: 1.5 },
                textAlign: 'left'
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  minWidth: 0,
                  flex: { md: '1 1 auto' }
                }}>
                <Box
                  sx={{
                    width: 4,
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    minHeight: { xs: 40, md: 52 },
                    borderRadius: 1,
                    bgcolor: 'primary.main'
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="p"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '0.62rem', sm: '0.68rem' },
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: 'primary.dark',
                      mb: 0.35,
                      opacity: 0.95
                    }}>
                    Au cœur de BatiKal — vos calculs
                  </Typography>
                  <Typography
                    component="h1"
                    sx={{
                      fontWeight: 900,
                      color: '#0f172a',
                      letterSpacing: '-0.03em',
                      lineHeight: 1.08,
                      fontSize: { xs: '1.65rem', sm: '1.95rem', md: '2.15rem' },
                      wordBreak: 'break-word'
                    }}>
                    Calculs
                  </Typography>
                </Box>
              </Box>
              <Typography
                component="p"
                sx={{
                  color: '#334155',
                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  lineHeight: 1.55,
                  fontWeight: 500,
                  maxWidth: { md: 540 },
                  flex: { md: '1 1 44%' },
                  m: 0,
                  pl: { xs: 0.25, md: 0 }
                }}>
                Liste complète des <Box component="strong" sx={{ color: '#0f172a', fontWeight: 700 }}>outils</Box> — parcourez-les
                par domaine ou par ordre alphabétique. Filtrez par le nom affiché à l’écran.
              </Typography>
            </Box>
          </Paper>

        <Stack
          spacing={2}
          sx={{ mb: 3, maxWidth: { xs: '100%', md: theme.breakpoints.values.md }, mx: { xs: 0, md: 'auto' } }}>
          <TextField
            fullWidth
            placeholder="Filtrer par nom d’outil…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 22 }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper'
              }
            }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5 }}>
              Affichage
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={groupMode}
              onChange={(_, v: GroupMode | null) => v != null && setGroupMode(v)}
              sx={{
                gap: 1,
                '& .MuiToggleButtonGroup-grouped': {
                  borderRadius: '12px !important',
                  border: 'none !important'
                },
                '& .MuiToggleButton-root': {
                  px: 2.5,
                  py: 1.1,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  borderRadius: '12px',
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.55)} !important`,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.18)
                  },
                  '&.Mui-selected': {
                    color: theme.palette.primary.contrastText,
                    bgcolor: theme.palette.primary.main,
                    borderColor: `${theme.palette.primary.dark} !important`,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      borderColor: `${theme.palette.primary.dark} !important`
                    }
                  }
                }
              }}>
              <ToggleButton value="section">
                <AccountTreeOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />
                Par section
              </ToggleButton>
              <ToggleButton value="alpha">
                <SortByAlphaIcon sx={{ fontSize: 18, mr: 0.75 }} />
                Ordre alphabétique
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: 'text.disabled', ml: { xs: 0, sm: 'auto' } }}>
              {filtered.length} calcul{filtered.length !== 1 ? 's' : ''}
              {filter.trim() ? ` sur ${CATALOG.length}` : ''}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ maxWidth: { xs: '100%', md: theme.breakpoints.values.md }, mx: { xs: 0, md: 'auto' } }}>
        {filtered.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              bgcolor: 'grey.50'
            }}>
            <Typography color="text.secondary">Aucun calcul ne correspond à cette recherche.</Typography>
          </Paper>
        ) : (
          <>
            {rangeLabel != null && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Affichage {rangeLabel.from}–{rangeLabel.to} sur {totalCount}
              </Typography>
            )}
            {groupMode === 'section' && pageSectionSlice ? (
              <Stack spacing={3}>
                {pageSectionSlice.keys.map(sectionKey => (
                  <Box key={`${sectionKey}-${page}`}>
                    <Typography
                      variant="overline"
                      sx={{
                        display: 'block',
                        letterSpacing: '0.12em',
                        fontWeight: 800,
                        color: 'primary.main',
                        mb: 1.5,
                        pl: 0.25
                      }}>
                      {t(sectionKey as Sections)}
                    </Typography>
                    <Stack spacing={1.5}>{pageSectionSlice.g[sectionKey]!.map(renderRow)}</Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Stack spacing={1.5}>{pageItems.map(renderRow)}</Stack>
            )}

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Box>
            )}
          </>
        )}
        </Box>
        </Container>
      </Box>
    </Page>
  )
}

export default memo(AllCalculsPage)
